import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { GUEST_STATUS_LABELS, type PublicSessionStatus } from '@pulsepoint/shared';
import { useState } from 'react';
import { ApiError } from '../../api/client';
import { useGuestSessions, useRefund, type GuestRow } from '../../api/payToCharge';
import { usePermission } from '../../auth/permissions';
import { DataTable, type Column } from '../../components/DataTable';
import { useDialog, type DialogProps } from '../../components/dialogs/DialogProvider';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { StatusLabel, type StatusTone } from '../../components/StatusLabel';
import { useToast } from '../../components/Toast';
import { formatDateTime } from '../../lib/format';
import { placeOf } from '../../lib/place';
import { colours } from '../../theme';
import { kwhOf, PERIODS, periodRange, type Period } from '../sessions/format';

const dollars = (cents: number) => `$ ${(cents / 100).toFixed(2)}`;
const when = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
const TONE: Record<PublicSessionStatus, StatusTone> = {
  created: 'off',
  authorised: 'live',
  charging: 'live',
  stopping: 'live',
  completed: 'neutral',
  captured: 'success',
  cancelled: 'off',
  failed: 'fault',
};
const choice = (r: Pick<GuestRow, 'limitType' | 'limitValue'>) =>
  r.limitType === 'energy' ? `${r.limitValue / 1000} kWh` : r.limitType === 'money' ? dollars(r.limitValue) : r.limitValue < 60 ? `${r.limitValue} min` : `${r.limitValue / 60} h`;
const where = (r: GuestRow) => [placeOf(r), r.bay ? `Bay ${r.bay}` : null].filter(Boolean).join(' · ');

/** Public charging: guests' sessions and payments, and refunds. (QR stickers are in Settings.) */
export function PayToChargePage() {
  const [period, setPeriod] = useState<Period>('month');
  const [status, setStatus] = useState<PublicSessionStatus | ''>('');
  const [page, setPage] = useState(1);
  const q = { ...periodRange(period, { from: '', to: '' }), status: status || undefined, page, pageSize: 25 };
  const list = useGuestSessions(q);
  const dialog = useDialog();
  const d = list.data;

  const columns: Column<GuestRow>[] = [
    { key: 'started', header: 'Started', render: (r) => <Typography className="num" sx={{ whiteSpace: 'nowrap' }}>{when.format(new Date(r.startedAt ?? r.createdAt))}</Typography> },
    {
      key: 'charger',
      header: 'Charger',
      render: (r) => (
        <Box>
          <Typography sx={{ fontWeight: 500 }}>{r.chargerName}</Typography>
          <Typography variant="body2" color="textSecondary">{where(r)}</Typography>
        </Box>
      ),
    },
    { key: 'chose', header: 'Chose', hideBelow: 'md', render: (r) => choice(r) },
    { key: 'kwh', header: 'kWh', align: 'right', hideBelow: 'sm', render: (r) => <span className="num">{kwhOf(r.energyWh ?? r.liveEnergyWh)}</span> },
    {
      key: 'paid',
      header: 'Paid',
      align: 'right',
      render: (r) => (
        <Box>
          <Typography className="num" sx={{ fontWeight: 500 }}>{r.finalCents != null ? dollars(r.finalCents - r.refundedCents) : '—'}</Typography>
          {r.refundedCents > 0 && <Typography variant="body2" color="textSecondary" className="num">{dollars(r.refundedCents)} refunded</Typography>}
        </Box>
      ),
    },
    { key: 'status', header: 'Status', render: (r) => <StatusLabel tone={TONE[r.status]}>{GUEST_STATUS_LABELS[r.status]}</StatusLabel> },
  ];

  const lead = d ? `${d.count} guest ${d.count === 1 ? 'session' : 'sessions'} · ${dollars(d.paidCents - d.refundedCents)} paid · ${kwhOf(d.energyWh, 1)} kWh${d.open ? ` · ${d.open} in progress` : ''}` : ' ';

  return (
    <>
      <PageHeader title="Public charging." lead={lead} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
        <TextField select size="small" value={period} onChange={(e) => { setPeriod(e.target.value as Period); setPage(1); }} sx={{ width: { xs: '100%', sm: 200 } }} slotProps={{ htmlInput: { 'aria-label': 'When' } }}>
          {PERIODS.filter((p) => p.value !== 'custom').map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" value={status} onChange={(e) => { setStatus(e.target.value as PublicSessionStatus | ''); setPage(1); }} sx={{ width: { xs: '100%', sm: 240 } }} slotProps={{ htmlInput: { 'aria-label': 'Status' }, select: { displayEmpty: true } }}>
          <MenuItem value="">Any status</MenuItem>
          {(['charging', 'completed', 'captured', 'cancelled', 'failed'] as const).map((s) => <MenuItem key={s} value={s}>{GUEST_STATUS_LABELS[s]}</MenuItem>)}
        </TextField>
      </Stack>

      <DataTable
        label="Guest sessions"
        rows={d?.rows}
        columns={columns}
        rowKey={(r) => r.id}
        loading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        onRowClick={(r) => void dialog.open(GuestSessionDialog, { row: r })}
        server={{ total: d?.count ?? 0, page, pageSize: 25, onPage: setPage, sort: { key: 'started', direction: 'desc' }, onSort: () => undefined }}
        empty={<EmptyState title="No guest sessions here">When guests charge and pay with a QR code, their sessions and payments show here.</EmptyState>}
      />

    </>
  );
}

/** One guest session: what they chose, used and paid, and a refund for admins. */
function GuestSessionDialog({ row: r, close }: { row: GuestRow } & DialogProps) {
  const canRefund = usePermission('billing.run');
  const refund = useRefund();
  const toast = useToast();
  const left = (r.finalCents ?? 0) - r.refundedCents;
  const [amount, setAmount] = useState(left > 0 ? (left / 100).toFixed(2) : '');
  const [error, setError] = useState('');
  const facts: [string, string][] = [
    ['Status', GUEST_STATUS_LABELS[r.status]],
    ['Charger', `${r.chargerName} · ${where(r)}`],
    ['Started', r.startedAt ? formatDateTime(r.startedAt) : 'Did not start'],
    ['Finished', r.stoppedAt ? formatDateTime(r.stoppedAt) : '—'],
    ['Chose', choice(r)],
    ['Hold', dollars(r.holdCents)],
    ['Energy', `${kwhOf(r.energyWh ?? r.liveEnergyWh, 3)} kWh${r.energyCents != null ? `, ${dollars(r.energyCents)}` : ''}`],
    ['Idle fee', r.idleCents ? dollars(r.idleCents) : '—'],
    ['Paid', r.finalCents != null ? dollars(r.finalCents) : '—'],
    ...(r.refundedCents ? ([['Refunded', dollars(r.refundedCents)]] as [string, string][]) : []),
    ['Paid with', r.paymentMethod ?? '—'],
    ['Receipt', r.receiptNumber ? `${r.receiptNumber}${r.hasEmail ? ', emailed' : ''}` : '—'],
    ...(r.message ? ([['Note', r.message]] as [string, string][]) : []),
  ];

  const doRefund = async () => {
    setError('');
    const cents = Math.round(Number(amount) * 100);
    if (!cents || cents < 1 || cents > left) return setError(`Between $ 0.01 and ${dollars(left)}`);
    try {
      await refund.mutateAsync({ id: r.id, amountCents: cents });
      toast(`${dollars(cents)} refunded to the guest's card.`);
      close();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The refund failed. Please try again.');
    }
  };

  return (
    <Dialog open onClose={() => close()} fullWidth maxWidth="sm">
      <DialogTitle>Guest session</DialogTitle>
      <DialogContent>
        <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' }, columnGap: 3, rowGap: { xs: 0.25, sm: 1 } }}>
          {facts.map(([k, v]) => (
            <Box key={k} sx={{ display: 'contents' }}>
              <Typography component="dt" color="textSecondary" sx={{ mt: { xs: 1, sm: 0 } }}>{k}</Typography>
              <Typography component="dd" className="num" sx={{ m: 0 }}>{v}</Typography>
            </Box>
          ))}
        </Box>
        {canRefund && r.status === 'captured' && left > 0 && (
          <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${colours.hairline}` }}>
            <Typography sx={{ fontWeight: 500, mb: 1.5 }}>Refund</Typography>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
              <TextField size="small" label="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} error={!!error} helperText={error || `Up to ${dollars(left)}`} slotProps={{ htmlInput: { inputMode: 'decimal' }, input: { startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> } }} />
              <Button onClick={() => void doRefund()} disabled={refund.isPending}>Refund to their card</Button>
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={() => close()}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
